import React from "react";
import { graphql, PageProps } from "gatsby";
import {
  SanityMultilingualBlockContent,
  TermsAndConditionsQuery
} from "types/graphql-types";
import {
  SectionContainer,
  SectionTitleHeading
} from "@silversea-ssc/ui-components-react";
import OpenGraph from "components/SEO/OpenGraph";
import PageBuilderOpeningText from "components/PageBuilder/PageBuilderOpeningText";
import { useGeolocation } from "../utils/localization/geolocation/GeolocationContext";
import { filterContent } from "./Offer/utils";
import { BlockContent } from "components/BlockContent";
import TextBox from "components/TextBox/TextBox";
import { useTermsAndConditions } from "../utils/useRemoteOffers";
import { useLanguage } from "../utils/localization/languages/I18nContext";
import { offerPlaceholderResolver } from "components/OfferTokenizedContent/utils";
import { TextStyle } from "@silversea-ssc/ui-components-react";
import { Trans } from "@lingui/macro";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import Uppercase from "components/Text/Uppercase";

const TermsAndConditionsTemplate: React.FC<
  PageProps<TermsAndConditionsQuery>
> = ({ data, location }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const page = data.page!;
  const { countryCode } = useGeolocation();
  const language = useLanguage();
  const { data: termsAndConditions } = useTermsAndConditions(
    location,
    language
  );
  const localisedGeneralCondition = filterContent<SanityMultilingualBlockContent>(
    undefined,
    countryCode,
    page.generalConditions
  );
  return (
    <React.Fragment>
      <OpenGraph
        openGraph={data.page?.metadata?.openGraph}
        excludeFromSitemap={data.page?.metadata?.excludeFromSitemap || false}
      />
      <SectionContainer spacingTop="regular" spacingBottom="regular">
        <PageBuilderOpeningText {...page.heading} ctaButtons={[]} />
      </SectionContainer>
      <SectionContainer
        spacingTop={null}
        spacingBottom="regular"
        dataContentName="terms_and_conditions"
      >
        <TextBox
          content={localisedGeneralCondition?.localized}
          fontSize={"small"}
        />
      </SectionContainer>
      {termsAndConditions && (
        <SectionContainer
          spacingTop={null}
          spacingBottom="regular"
          dataContentName="terms_and_conditions_offers"
        >
          <SectionTitle variant="wide">
            <SectionTitleHeading>
              <Uppercase>
                <Trans>Exclusive Offers Terms & Conditions</Trans>
              </Uppercase>
            </SectionTitleHeading>
          </SectionTitle>
          <TextStyle fontSize={"small"}>
            <Trans>
              Please note that 1 night pre cruise hotel and transfers between
              airport, hotel and ship are not available on cruises with the
              following code: xxxxxxxxSxx.
            </Trans>
          </TextStyle>
          {termsAndConditions.map((tac, index) => (
            <TextStyle fontSize={"small"} key={index}>
              <h3>
                <Uppercase>
                  {tac.termsAndConditions.title && (
                    <BlockContent
                      blocks={tac.termsAndConditions.title[language.sanityCode]}
                      placeholderResolver={offerPlaceholderResolver(
                        tac.tokenValues
                      )}
                    />
                  )}
                </Uppercase>
              </h3>
              {tac.termsAndConditions.content && (
                <TextBox
                  placeholderResolver={offerPlaceholderResolver(
                    tac.tokenValues
                  )}
                  content={tac.termsAndConditions.content[language.sanityCode]}
                  fontSize={"small"}
                />
              )}
            </TextStyle>
          ))}
        </SectionContainer>
      )}
    </React.Fragment>
  );
};

export const query = graphql`
  query TermsAndConditions($id: String!) {
    page: sanityTermsAndConditions(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }
      id
      generalConditions {
        content {
          localized
          rawLocalized
        }
        locationFilters {
          locationFilter
          _type
        }
      }
      heading {
        title {
          localized
        }
        content {
          localized
          rawLocalized
        }
      }
    }
  }
`;

export default TermsAndConditionsTemplate;
